import { getRouteApi } from '@tanstack/react-router';
import { APPLICANT_ATTRIBUTION_BOTH, CHILD_BENEFIT_TYPES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { APPLICANT_ATTRIBUTION_BOTH_TYPE } from 'cpool-constants';
import { createId } from 'cpool-utils';
import { useFieldArray, useFormContext } from 'react-hook-form';

import Child from './Child.tsx';
import ItemAddButton from '../General/ItemAddButton.tsx';
import { KINDERGELD_HOEHE } from '~/constants.ts';
import { hide } from '~/features/leadDetails/components/Overview/CustomerData/General/utils/hide.ts';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    hasSecondApplicant: boolean;
    secondApplicantHidden?: boolean;
};

const Children: React.FC<Props> = ({ hasSecondApplicant, secondApplicantHidden = false }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { getValues } = useFormContext<SchemaLivingSituationForm>();

    const { append, fields, remove, update } = useFieldArray<SchemaLivingSituationForm, 'kinder', 'childId'>({
        keyName: 'childId',
        name: 'kinder',
    });

    const addChild = () => {
        const entry: Required<SchemaLivingSituationForm>['kinder'][number] = {
            alter: null,
            gehoertZuAntragsteller: hasSecondApplicant
                ? APPLICANT_ATTRIBUTION_BOTH.BEIDE
                : APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
            id: createId(),
            kindergeldFuer: CHILD_BENEFIT_TYPES.ERSTES_ODER_ZWEITES_KIND,
            kindergeldHoehe: KINDERGELD_HOEHE,
            unterhaltseinnahmenMonatlich: null,
        };
        append(entry);
    };

    const changeApplicantAttribution = (index: number, value: APPLICANT_ATTRIBUTION_BOTH_TYPE) => {
        if (fields[index].gehoertZuAntragsteller === value) return;
        const child = getValues(`kinder.${index}`);
        update(index, { ...child, gehoertZuAntragsteller: value });
    };

    const removeChild = (index: number) => {
        remove(index);
    };

    // Without a co-applicant there is no attribution control, so children left over from a
    // removed co-applicant would be uneditable and are therefore not listed at all.
    const listedChildren = fields
        .map((child, childIndex) => ({ child, childIndex }))
        .filter(
            ({ child }) =>
                hasSecondApplicant || child.gehoertZuAntragsteller !== APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2,
        );

    return (
        <section className="space-y-2" data-testid="children">
            {listedChildren.map(({ child, childIndex }, position) => (
                <div
                    key={child.childId}
                    style={hide(
                        secondApplicantHidden &&
                            child.gehoertZuAntragsteller === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2,
                    )}
                >
                    <Child
                        childIndex={childIndex}
                        childNumber={position + 1}
                        removeChild={removeChild}
                        onApplicantAttributionChange={changeApplicantAttribution}
                        hasSecondApplicant={hasSecondApplicant}
                    />
                </div>
            ))}
            <ItemAddButton
                onClick={addChild}
                translationPath="LIVING_SITUATION_DATA.CHILDREN.ADD_CHILD"
                disabled={
                    !canExecuteApplicantCommand({
                        applicant: 1,
                        commandName: LOAN_REQUEST_COMMANDS.addChild,
                        permissions,
                    })
                }
            />
        </section>
    );
};

export default Children;
